import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import Swal from "sweetalert2";
import { LoaderScreen } from "../components/LoaderScreen";
import request from "../../Helpers/API";
import { TIPOS_SOLICITUD, IMPACTOS_NEGOCIO, useGestionTI } from "./gestionti-constants";

const ESTADO_INICIAL = {
    tipo: "Incidencia",
    titulo: "",
    idmodulo: "",
    descripcion: "",
    pregunta_informacion: "",
    pregunta_objetivo_negocio: "",
    pregunta_decisiones: "",
    pregunta_frecuencia: "",
    pregunta_uso_compartido: "",
    impacto_control_interno: false,
    impacto_normativo: false,
    impacto_financiero: false,
    impacto_comentario: "",
};

export const NuevaSolicitudScreen = () => {
    const history = useHistory();
    const { rol, cargandoRol } = useGestionTI();

    const [form, setForm]       = useState(ESTADO_INICIAL);
    const [modulos, setModulos] = useState([]);
    const [loader, setLoader]   = useState({ mostrar: true, alerta: false });
    const [guardando, setGuardando] = useState(false);

    const requiereImpacto   = form.tipo === "Nuevo desarrollo" || form.tipo === "Mejora";
    // Las 5 preguntas de negocio son del proceso de acceso a menús/reportes
    // existentes -- no aplican a Incidencia, Nuevo desarrollo ni Mejora.
    const requierePreguntas = form.tipo === "Acceso a información";

    // Developer no debe poder crear solicitudes (solo Usuario y Admin, ver
    // matriz de permisos del proceso). El menú ya lo oculta, esto es
    // segunda línea de defensa en el propio front.
    useEffect(() => {
        if (!cargandoRol && rol === "Developer") {
            Swal.fire("GESTIÓN TI", "Tu rol no puede crear solicitudes.", "warning");
            history.replace("/GestionTI/Principal");
        }
    }, [cargandoRol, rol, history]);

    useEffect(() => {
        request
            .get("Menu/DropDownList")
            .then((result) => {
                setModulos(result ?? []);
                setLoader({ mostrar: false, alerta: false });
            })
            .catch((error) => {
                setLoader({ mostrar: false, alerta: false });
                Swal.fire("GESTIÓN TI", "No se pudo cargar el catálogo de módulos.", "error");
            });
    }, []);

    const set = (campo) => (e) => {
        const valor = e?.target?.type === "checkbox" ? e.target.checked : e.target.value;
        setForm((f) => ({ ...f, [campo]: valor }));
    };

    const onCancelar = () => history.push("/GestionTI/Principal");

    const validar = () => {
        const requeridos = [
            ["titulo", "Título"],
            ["idmodulo", "Módulo"],
            ["descripcion", "Descripción"],
        ];
        if (requierePreguntas) {
            requeridos.push(
                ["pregunta_informacion", "¿Qué información estás solicitando consultar?"],
                ["pregunta_objetivo_negocio", "¿Cuál es el objetivo de negocio?"],
                ["pregunta_decisiones", "¿Qué decisiones o acciones tomarás con base en esta información?"],
                ["pregunta_frecuencia", "¿Con qué frecuencia requieres consultarla?"],
                ["pregunta_uso_compartido", "¿Es solo para ti o se comparte con tu área?"],
            );
        }
        for (const [campo, etiqueta] of requeridos) {
            if (!String(form[campo] ?? "").trim()) {
                Swal.fire("Falta información", `El campo "${etiqueta}" es obligatorio.`, "warning");
                return false;
            }
        }
        if (requiereImpacto) {
            const algunImpacto = form.impacto_control_interno || form.impacto_normativo || form.impacto_financiero;
            if (!algunImpacto) {
                Swal.fire("Falta información", "Selecciona al menos un impacto de negocio (Control interno, Normativo o Financiero).", "warning");
                return false;
            }
            if (!String(form.impacto_comentario ?? "").trim()) {
                Swal.fire("Falta información", "Agrega un comentario del impacto de negocio.", "warning");
                return false;
            }
        }
        return true;
    };

    const onGuardar = () => {
        if (!validar()) return;

        const payload = {
            ...form,
            idmodulo: Number(form.idmodulo),
            pregunta_informacion: requierePreguntas ? form.pregunta_informacion : null,
            pregunta_objetivo_negocio: requierePreguntas ? form.pregunta_objetivo_negocio : null,
            pregunta_decisiones: requierePreguntas ? form.pregunta_decisiones : null,
            pregunta_frecuencia: requierePreguntas ? form.pregunta_frecuencia : null,
            pregunta_uso_compartido: requierePreguntas ? form.pregunta_uso_compartido : null,
            impacto_control_interno: requiereImpacto ? form.impacto_control_interno : false,
            impacto_normativo: requiereImpacto ? form.impacto_normativo : false,
            impacto_financiero: requiereImpacto ? form.impacto_financiero : false,
            impacto_comentario: requiereImpacto ? form.impacto_comentario : null,
        };

        setGuardando(true);
        request
            .post("SolicitudesTI/Crear", payload)
            .then((result) => {
                setGuardando(false);
                Swal.fire("Solicitud creada", "Tu solicitud fue registrada correctamente.", "success")
                    .then(() => history.push("/GestionTI/Principal"));
            })
            .catch((error) => {
                setGuardando(false);
                Swal.fire("GESTIÓN TI", error?.mensaje ?? "No se pudo crear la solicitud.", "error");
            });
    };

    if (loader.mostrar) {
        return <LoaderScreen loader={loader} />;
    }

    return (
        <div className="gestionti-vista">
            <div className="gestionti-vista-header">
                <span className="gestionti-vista-titulo">Nueva Solicitud</span>
            </div>

            <div className="gestionti-form scroll-4">

                {/* ── Tipo de solicitud ────────────────────────────────────── */}
                <div className="gestionti-form-section">
                    <label className="gestionti-field-label">Tipo de solicitud</label>
                    <div className="gestionti-tipo-group">
                        {TIPOS_SOLICITUD.map((t) => (
                            <button
                                type="button"
                                key={t}
                                className={
                                    "gestionti-tipo-option" +
                                    (form.tipo === t ? " gestionti-tipo-option--activo" : "")
                                }
                                onClick={() => setForm((f) => ({ ...f, tipo: t }))}
                            >
                                {t}
                            </button>
                        ))}
                    </div>
                </div>

                {/* ── Datos generales ──────────────────────────────────────── */}
                <div className="gestionti-form-section">
                    <div className="gestionti-form-row">
                        <div className="gestionti-field gestionti-field--grow">
                            <label className="gestionti-field-label">Título</label>
                            <input
                                className="gestionti-input"
                                type="text"
                                maxLength={200}
                                value={form.titulo}
                                onChange={set("titulo")}
                                placeholder="Resumen breve de la solicitud"
                            />
                        </div>
                        <div className="gestionti-field">
                            <label className="gestionti-field-label">Módulo</label>
                            <select className="gestionti-select" value={form.idmodulo} onChange={set("idmodulo")}>
                                <option value="">Selecciona…</option>
                                {modulos.map((m) => (
                                    <option key={m.id} value={m.id}>{m.display}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="gestionti-field">
                        <label className="gestionti-field-label">
                            Descripción{form.tipo === "Incidencia" ? " — incluye dónde se origina el error" : ""}
                        </label>
                        <textarea
                            className="gestionti-textarea"
                            rows={3}
                            value={form.descripcion}
                            onChange={set("descripcion")}
                        />
                    </div>
                </div>

                {/* ── Las 5 preguntas (solo Acceso a información) ─────────────── */}
                {requierePreguntas && (
                    <div className="gestionti-form-section">
                        <div className="gestionti-form-section-titulo">Justificación del acceso</div>

                        <div className="gestionti-field">
                            <label className="gestionti-field-label">¿Qué información estás solicitando consultar?</label>
                            <textarea className="gestionti-textarea" rows={2} value={form.pregunta_informacion} onChange={set("pregunta_informacion")} />
                        </div>
                        <div className="gestionti-field">
                            <label className="gestionti-field-label">¿Cuál es el objetivo de negocio para el que la utilizarás?</label>
                            <textarea className="gestionti-textarea" rows={2} value={form.pregunta_objetivo_negocio} onChange={set("pregunta_objetivo_negocio")} />
                        </div>
                        <div className="gestionti-field">
                            <label className="gestionti-field-label">¿Qué decisiones o acciones tomarás con base en esta información?</label>
                            <textarea className="gestionti-textarea" rows={2} value={form.pregunta_decisiones} onChange={set("pregunta_decisiones")} />
                        </div>
                        <div className="gestionti-form-row">
                            <div className="gestionti-field gestionti-field--grow">
                                <label className="gestionti-field-label">¿Con qué frecuencia requieres consultarla?</label>
                                <input className="gestionti-input" type="text" value={form.pregunta_frecuencia} onChange={set("pregunta_frecuencia")} />
                            </div>
                            <div className="gestionti-field gestionti-field--grow">
                                <label className="gestionti-field-label">¿Es solo para ti o se comparte con tu área?</label>
                                <input className="gestionti-input" type="text" value={form.pregunta_uso_compartido} onChange={set("pregunta_uso_compartido")} />
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Impacto de negocio (solo Nuevo desarrollo / Mejora) ──────── */}
                {requiereImpacto && (
                    <div className="gestionti-form-section">
                        <div className="gestionti-form-section-titulo">Impacto en el negocio</div>
                        <div className="gestionti-checkbox-group">
                            {IMPACTOS_NEGOCIO.map(({ key, label }) => (
                                <label key={key} className="gestionti-checkbox-option">
                                    <input type="checkbox" checked={form[key]} onChange={set(key)} />
                                    {label}
                                </label>
                            ))}
                        </div>
                        <div className="gestionti-field">
                            <label className="gestionti-field-label">Comentario</label>
                            <textarea className="gestionti-textarea" rows={2} value={form.impacto_comentario} onChange={set("impacto_comentario")} />
                        </div>
                    </div>
                )}

                <div className="gestionti-form-actions">
                    <button className="gestionti-btn-secondary" type="button" onClick={onCancelar} disabled={guardando}>
                        Cancelar
                    </button>
                    <button className="gestionti-btn-primary" type="button" onClick={onGuardar} disabled={guardando}>
                        {guardando ? "Guardando…" : "Crear solicitud"}
                    </button>
                </div>
            </div>
        </div>
    );
};
